/* eslint-disable react/no-danger -- when updating this component, consider replacing dangerous jsx properties */
import type { ReactNode } from 'react';
import React, { PureComponent, Fragment } from 'react';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { evalLegacyConnectInlineScripts } from '@confluence/connect-utils';
import { LegacyUserElementsHover } from '@confluence/profile/entry-points/LegacyUserElementsHover.ts';
import { requireSuperBatch } from '@confluence/wrm/entry-points/requireSuperBatchHelpers.ts';
import { realForceRequireLegacyWRM } from '@confluence/wrm/entry-points/requireLegacyWRMHelpers.ts';
import { ScrollToHashLinkHandler } from '@confluence/scroll/entry-points/ScrollToHashLinkHandler.ts';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import {
	loadMacroWebResources,
	type WebResources,
} from '@confluence/fabric-extension-lib/entry-points/macro-loaders.ts';

import { loadJSDependencies, isRequiredModuleLoaded } from './tinyMCE/loadJSDependencies.ts';
import {
	getElementFromHash,
	isElementInViewport,
	followHashAnchorIfNeeded,
} from './tinyMCE/handle-anchors.ts';
import { MacrosByIdsQuery } from './tinyMCE/MacrosByIdsQuery.graphql.ts';
import type {
	MacrosByIdsQuery as MacrosByIdsQueryType,
	MacrosByIdsQueryVariables,
	MacrosByIdsQuery_confluence_macrosByIds,
	MacrosByIdsQuery_confluence_macrosByIds_array,
} from './tinyMCE/__types__/MacrosByIdsQuery.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

const TIMEOUT_TO_STOP_CHECKING_FOR_MUTATIONS_AND_INTERACTION = 20000;
const userInteractionEvents = ['mousedown', 'keydown', 'wheel', 'touchstart'];

type TinyMCEClientRendererProps = {
	body: string;
	jsDependencies: string;
	cssDependencies: string;

	getLegacyContentExtensions?: () => any;
	renderWhenReady?: () => ReactNode;
	isAnonymous: boolean;
	scrollTo: string;
	cloudId: string;
	contentId: string;
};

type TinyMCEClientRendererState = {
	contentNode: string;
};

export class TinyMCEClientRendererComponent extends PureComponent<
	TinyMCEClientRendererProps,
	TinyMCEClientRendererState
> {
	private isLCEReady: Promise<void>;
	private setLCEReady: () => void;
	private MAX_CONNECT_STATIC_QUERY_MACRO_BATCH_SIZE: number;

	constructor(props: any) {
		super(props);

		this.state = { contentNode: '' };

		// NOTE: this construction is needed to ensure that we only bootstrap legacy
		// content extensions only when corresponding callback is available. Depending
		// on timing it may be available later than the content, so we don't want to
		// delay the content rendering and only let the initialization to wait until
		// `getLegacyContentExtensions` callback is available.
		this.setLCEReady = () => {};
		this.isLCEReady = new Promise((resolve) => {
			this.setLCEReady = resolve;
		});
		this.MAX_CONNECT_STATIC_QUERY_MACRO_BATCH_SIZE = 8;

		if (window?.__SSR_RENDERED__) {
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
			const el = document.getElementById('tiny-js-container');
			if (el !== null) {
				el.innerHTML = '';
			}
		}
	}

	componentDidMount(): void {
		this._isMounted = true;

		const { jsDependencies } = this.props;

		void this._bootstrapRichContent(jsDependencies);
		this._setBodyContainer(this._bodyContainer.current);
		void realForceRequireLegacyWRM(
			['com.atlassian.confluence.plugins.confluence-macro-browser:macro-browser-css'],
			() => {},
			() => {},
		);
	}

	componentDidUpdate(prevProps: any): void {
		const { body, jsDependencies, getLegacyContentExtensions } = this.props;

		if (body !== prevProps.body) {
			void this._bootstrapRichContent(jsDependencies);
		}

		if (!prevProps.getLegacyContentExtensions && getLegacyContentExtensions) {
			this.setLCEReady();
		}
	}

	componentWillUnmount(): void {
		this._isMounted = false;
		this._stopCheckingForMutationsAndInteraction();
	}

	private _isMounted: boolean = false;
	private mutationObserver: MutationObserver | null = null;

	scrollDebounceTimerHandle = 0;
	userInteractionEventsRegistered = false;
	mutationEndTimer = 0;

	// DOM node that has Page Content
	_bodyContainer: React.RefObject<any> = React.createRef<any>();

	// DOM node where JS scripts are appended.
	_scriptContainer: React.RefObject<any> = React.createRef<any>();

	_setBodyContainer(bodyContainer: any): void {
		this.setState({ contentNode: bodyContainer });
	}

	async _bootstrapRichContent(jsDependencies: any): Promise<void> {
		await requireSuperBatch();
		if (this._scriptContainer.current) {
			await loadJSDependencies(
				[
					'wr!com.atlassian.confluence.plugins.confluence-jira-content:confluence-jira-content-loader',
					'wr!com.atlassian.confluence.plugins.confluence-view-file-macro:view-file-macro-resources',
					'wr!confluence.web.resources:view-content',
					// CONFSIM-1578 This is required in order for Page Properties Report Macro with checkboxes to work
					'wr!com.atlassian.confluence.plugins.confluence-inline-tasks:inline-tasks-styles',
					// CONFSIM-1697 Required for sticky table header to work
					'wr!com.atlassian.confluence.plugins.sticky-table-headers:stickytableheaders-resources',
				],
				// When macro SSR is turned on the content specific scripts were loaded in SSR result in TinyMCEServerFullRenderer.tsx
				// They were put in content div so that when transition they can be removed then reload again.
				window['__SSR_RENDERED__'] ? '' : jsDependencies,
				this._scriptContainer.current,
			);

			// There is a chance that the component has already gone by the time
			// everything is loaded. In this case we don't do anything.
			if (this._isMounted && this._bodyContainer.current) {
				/**
				 * When a _sortable_ table is initialized, the body of the table is removed as part
				 * of the sorting process. This prevents Connect apps in the table from initializing
				 * correctly, as the iframe gets destroyed when the body is removed. To prevent this,
				 * we need to await the initialization of the table before initializing Connect apps.
				 */
				await this._initializeTables();
				await evalLegacyConnectInlineScripts(this._bodyContainer.current);
				this._onContentLoaded();
			}
		}
	}

	_initializeCodeMacroScript = (): void => {
		try {
			if (this._bodyContainer.current?.querySelector('.code.conf-macro')) {
				if (isRequiredModuleLoaded('confluence/code-macro/async-loader')) {
					const AsyncLoader = window.require('confluence/code-macro/async-loader');
					AsyncLoader?.();
				}

				if (isRequiredModuleLoaded('confluence/code-macro/collapse-source')) {
					const CollapseSource = window.require('confluence/code-macro/collapse-source');
					CollapseSource?.init();
				}
			}
		} catch (e) {
			if (process.env.NODE_ENV !== 'production') {
				// eslint-disable-next-line no-console
				console.warn('An error occurred initializing a code macro.', e);
			}
		}
	};

	_onContentLoaded = (): void => {
		void this._initializeConnectStaticMacros();
		this._initializeCalendarIfNeeded();
		this._initializeCodeMacroScript();
		this._setupDeeplinkChecking();
	};

	/**
	 * HOT-84603
	 * ---------
	 * Current implementation of the Team Calendars Plugin does not account for
	 * a SPA transition. It only loads the calendars once and forgets about
	 * re-initializing them once the page is unmounted. For this, we need to
	 * invoke the assets when necessary and initialize the calendars.
	 * @private
	 */
	_initializeCalendarIfNeeded(): void {
		const contentHasTeamCalendars =
			this._bodyContainer.current &&
			this._bodyContainer.current.querySelector('.calendar-container');
		if (contentHasTeamCalendars) {
			if (isRequiredModuleLoaded('tc/init-resources')) {
				try {
					window.require('tc/init-resources').requireResources();
				} catch (e) {
					if (process.env.NODE_ENV !== 'production') {
						// eslint-disable-next-line no-console
						console.warn('An error occurred initializing a team calendar.', e);
					}
				}
			}
		}
	}

	async _initializeTables(): Promise<void> {
		if (!this._bodyContainer.current) {
			return;
		}

		// Only fetch assets required for table sorting if there's actually a table in the content.
		const tables = this._bodyContainer.current.querySelectorAll('table');
		if (tables.length === 0) {
			return;
		}

		if (this.props.getLegacyContentExtensions) {
			this.setLCEReady();
		}

		await this.isLCEReady;

		if (this.props.getLegacyContentExtensions) {
			const { loadSortableTable } = this.props.getLegacyContentExtensions();
			const sortableTable = await loadSortableTable();
			tables.forEach((table: any) => sortableTable.default.init(table));
		}
	}

	async _queryConnectStaticMacrosRenderedOutput(
		contentId: any,
		macroIds: any,
	): Promise<
		| readonly ({
				readonly macroId: string;
				readonly renderedMacro: {
					readonly value: string | null;
					readonly webResource: {
						readonly contexts: ReadonlyArray<string | null>;
						readonly keys: ReadonlyArray<string | null>;
						readonly tags: {
							readonly css: string | null;
							readonly js: string | null;
						} | null;
					} | null;
				} | null;
		  } | null)[]
		| null
	> {
		try {
			// Fetch the views for the given batch of macroIds
			const { data } = await queryInterop<MacrosByIdsQueryType, MacrosByIdsQueryVariables>({
				query: MacrosByIdsQuery,
				variables: {
					cloudId: this.props.cloudId,
					contentId,
					macroIds,
				},
			});

			return data?.['confluence_macrosByIds'];
		} catch (error) {
			markErrorAsHandled(error);
			getMonitoringClient().submitError(error, {
				attribution: Attribution.PAGE_EXPERIENCES,
			});
			return null;
		}
	}
	_createMacroWebResourceElement(html?: string): HTMLDivElement {
		// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
		const element = document.createElement('div');

		element.style.display = 'none';

		if (!!html) {
			element.innerHTML = html;
		}

		return element;
	}

	async _renderConnectStaticMacros(
		batch: Element[],
		macros: (MacrosByIdsQuery_confluence_macrosByIds_array | null | undefined)[],
	): Promise<void> {
		// Flatten the 2D array of arrays of macros rendered outputs to a 1D array of macros rendered outputs
		const macrosRenderedOutputs = macros.reduce<
			(MacrosByIdsQuery_confluence_macrosByIds | null | undefined)[]
		>((acc, current) => {
			if (current != null) {
				return [...acc, ...current];
			}
			return acc;
		}, []);

		for (const macroElement of batch) {
			const macroId = macroElement.getAttribute('data-macro-id');
			const macro = macrosRenderedOutputs?.find((m) => m?.macroId === macroId);

			const { value, webResource } = macro?.renderedMacro ?? {};
			const { js, css } = webResource?.tags ?? {};

			if (value !== undefined && value !== null) {
				// Replace placeholder with actual view
				macroElement.innerHTML = value;

				if (css) {
					// css tags should be placed inside content div so they can be cleaned up from page to page
					// Macro’s CSS needs to be placed before HTML to avoid FOUC
					macroElement.insertBefore(
						this._createMacroWebResourceElement(css),
						macroElement.firstChild,
					);
				}

				// This is for loading the macro's JS resources
				if (webResource && js) {
					const scriptContainer = this._createMacroWebResourceElement();
					macroElement.appendChild(scriptContainer);
					await loadMacroWebResources(
						webResource as WebResources,
						false,
						null,
						undefined,
						scriptContainer,
					);
				}
			}
		}
		await evalLegacyConnectInlineScripts(this._bodyContainer.current);
	}

	async _processBatchOfConnectStaticMacros(batch: Element[]): Promise<void> {
		const macrosIdsByContentIdMap: Map<string, string[]> = new Map();
		const macrosQueries: Promise<
			MacrosByIdsQuery_confluence_macrosByIds_array | null | undefined
		>[] = [];

		// Some macros are from an included page or excerpt meaning to get its rendered output we need to query
		// with its original contentId, not the current. We need to properly group each macro by contentId to
		// make the separate queries per contentId.
		//
		// Build a list of macroIds for each unique contentId
		batch.forEach((macroElement) => {
			// If data-content-id attribute is missing assuming the original container/contentId is that of the current page.
			const contentId =
				macroElement.getAttribute('data-container-content-id') ?? this.props.contentId;
			const macroId = macroElement.getAttribute('data-macro-id') ?? '';

			if (macrosIdsByContentIdMap.has(contentId)) {
				const currentList = macrosIdsByContentIdMap.get(contentId) ?? [];
				macrosIdsByContentIdMap.set(contentId, [...currentList, macroId]);
			} else {
				macrosIdsByContentIdMap.set(contentId, [macroId]);
			}
		});

		// Make the seperate queries for each contentId and push the result to the complete list
		for (const [contentId, macroIds] of macrosIdsByContentIdMap) {
			macrosQueries.push(this._queryConnectStaticMacrosRenderedOutput(contentId, macroIds));
		}
		// render the macros
		await this._renderConnectStaticMacros(batch, await Promise.all(macrosQueries));
	}

	async _initializeConnectStaticMacros(): Promise<void> {
		if (!this._bodyContainer.current) {
			return;
		}

		// Get all static connect macros.
		const connectStaticMacros: Element[] = Array.from(
			this._bodyContainer.current.querySelectorAll('.static-connect-macro-placeholder'),
		);

		// If none present on page, stop here.
		if (connectStaticMacros.length === 0) {
			return;
		}

		// In batches of MAX_CONNECT_STATIC_QUERY_MACRO_BATCH_SIZE, replace
		// the connect static macros placeholders with its actual rendered content.
		for (
			let i = 0;
			i < connectStaticMacros.length;
			i += this.MAX_CONNECT_STATIC_QUERY_MACRO_BATCH_SIZE
		) {
			const batch = connectStaticMacros.slice(
				i,
				i + this.MAX_CONNECT_STATIC_QUERY_MACRO_BATCH_SIZE,
			);
			await this._processBatchOfConnectStaticMacros(batch);
		}
	}

	/**
	 * "Deep linking" is a link not just to a page, but to a place (anchor)
	 * within that page.
	 *
	 * DeeplinkChecking waits for changes to the DOM after page load to settle
	 * before attempting to scroll to the targeted anchor.
	 *
	 * See CFE-3237 for more details
	 * https://stash.atlassian.com/projects/CONFCLOUD/repos/confluence-frontend/pull-requests/6835/overview
	 *
	 * @private
	 */
	_setupDeeplinkChecking(): void {
		const { scrollTo } = this.props;

		// Set-up deep link scrolling mechanism if there's a deep link requested
		if (scrollTo && scrollTo.indexOf('#') === 0 && this._bodyContainer.current) {
			const actualElementFromDOM = getElementFromHash(scrollTo);

			if (actualElementFromDOM) {
				this._setupMutationObserver(this._bodyContainer.current, actualElementFromDOM);
			}
		}
	}

	_stopCheckingForMutationsAndInteraction(): void {
		if (this.mutationObserver) {
			this.mutationObserver.disconnect();
			this.mutationObserver = null;
		}
		if (this.userInteractionEventsRegistered) {
			userInteractionEvents.forEach((name) => {
				window.removeEventListener(name, this._userInteracted, true);
			});
			this.userInteractionEventsRegistered = false;
		}
		if (this.mutationEndTimer !== 0) {
			clearTimeout(this.mutationEndTimer);
		}
		if (this.scrollDebounceTimerHandle !== 0) {
			clearTimeout(this.scrollDebounceTimerHandle);
		}
	}

	_userInteracted = (): void => {
		if (this.mutationObserver) {
			this.mutationObserver.disconnect();
			this.mutationObserver = null;
		}

		userInteractionEvents.forEach((name) => {
			window.removeEventListener(name, this._userInteracted, true);
		});
		this.userInteractionEventsRegistered = false;
	};

	/**
	 * Watch the content div for changes to it's children
	 * Whenever there is a change, check if we should scroll
	 * @param divRef: a reference to the div we're watching for change
	 * @param scrollTarget: the deep link reference element we're trying to scroll to
	 * @private
	 */
	_setupMutationObserver = (divRef: any, scrollTarget: any): void => {
		// Callback function to execute when mutations are observed
		const mutationCallback = () => {
			// Every time there's a change, restart the timer that ends watching for mutations.
			clearTimeout(this.mutationEndTimer);
			this.mutationEndTimer = setTimeout(() => {
				if (this.mutationObserver) {
					this.mutationObserver.disconnect();
				}
			}, TIMEOUT_TO_STOP_CHECKING_FOR_MUTATIONS_AND_INTERACTION) as unknown as number;

			if (!isElementInViewport(scrollTarget)) {
				followHashAnchorIfNeeded(scrollTarget, this.scrollDebounceTimerHandle);
			}
		};

		// Create an observer instance linked to the callback function
		this.mutationObserver = new MutationObserver(mutationCallback);

		// Start observing the target node for configured mutations
		this.mutationObserver.observe(divRef, {
			childList: true,
			subtree: true,
		});

		// Do an initial scroll
		if (!isElementInViewport(scrollTarget)) {
			followHashAnchorIfNeeded(scrollTarget, this.scrollDebounceTimerHandle);
		}

		// Listen for user interaction to end checking for changes
		if (!this.userInteractionEventsRegistered) {
			userInteractionEvents.forEach((name) => {
				window.addEventListener(name, this._userInteracted, true);
			});
			this.userInteractionEventsRegistered = true;
		}
	};

	render(): React.JSX.Element {
		const { body, cssDependencies, renderWhenReady } = this.props;
		const { contentNode } = this.state;

		return (
			<div
				id="content"
				data-testid="TinyMCERendererTestId"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="page view legacy-renderer"
			>
				<div
					data-testid="css"
					key="css-container"
					dangerouslySetInnerHTML={{ __html: `${cssDependencies}` }}
				/>
				<div
					dangerouslySetInnerHTML={{
						__html: body,
					}}
					ref={this._bodyContainer}
					id="main-content"
					data-inline-comments-target="true"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className="wiki-content"
				/>
				<ScrollToHashLinkHandler anchorContainer={this._bodyContainer} />
				<div
					key="js-container"
					data-testid="js"
					id="tiny-js-container"
					ref={this._scriptContainer}
				/>
				{/* put conditional ones to the end to avoid changing components order */}
				{contentNode && <LegacyUserElementsHover key="legacy-use-hover" node={contentNode} />}
				{renderWhenReady && (
					<Fragment>
						{renderWhenReady()}
						<RendererExtensionContext.Consumer>
							{({ EditorLoaderLoader }) => <EditorLoaderLoader />}
						</RendererExtensionContext.Consumer>
					</Fragment>
				)}
			</div>
		);
	}
}

export const TinyMCEClientRenderer: React.FC<TinyMCEClientRendererProps> = withErrorBoundary({
	attribution: Attribution.BACKBONE,
	attributes: { fabric: false },
})(TinyMCEClientRendererComponent);
